/*
  cf-accordion-anim.js — CleanFriend
  ----------------------------------
  Bloed aabne/luk-animation paa alle indholds-accordions.

  Baggrund: temaet bruger native <details>/<summary>, som browseren aabner og
  lukker helt uden overgang. Denne fil overtager klikket paa <summary>, saetter
  en eksplicit hoejde paa <details> og animerer den med Web Animations API.

  Rammer:  produktsidens collapsible tabs (.accordion details),
           Collapsible content-sektioner, cf-seo FAQ (details.cf-seo__qa),
           samarbejdssidens FAQ (details.cf-acc) og <details> i rich text.
  Rammer IKKE: menuer, mobil-drawer, mega-menu, filtre/facets, sprogvaelger,
           soegning, del-knap og kurv-drawer — de har deres egen adfaerd.

  Respekterer prefers-reduced-motion: der falder vi tilbage til browserens
  oejeblikkelige aaben/luk.

  Rettet 24/8 (glitch ved lukning):
   1) Indholdet fadede ud paa 200 ms, men hoejden var op til 460 ms om at
      kollapse. Fordi fade-animationen koerte med fill:'none', sprang
      opaciteten tilbage til 1 midtvejs — et tydeligt blink. Der fades nu kun
      ved aabning, hvor slutvaerdien er den samme som hviletilstanden.
   2) Hoejde-animationen koerte ogsaa med fill:'none'. I det oejeblik den var
      faerdig — foer vores kode naaede at fjerne [open] — faldt elementet et
      billede tilbage til den inline hoejde, altsaa fuld aaben hoejde. Nu
      koerer den med fill:'forwards' og bliver foerst annulleret, naar
      sluttilstanden er sat.
   3) Pilen drejede foerst tilbage til sidst. Dawns egen regel vandt over
      vores, fordi de havde samme specificitet og Dawns CSS indlaeses senere.
      Loest i cf-accordion-anim.css.
*/
(function () {
  'use strict';

  var INCLUDE = [
    '.accordion details',
    'details.cf-seo__qa',
    'details.cf-acc',
    '.rte details'
  ].join(',');

  var EXCLUDE = [
    'header-drawer',
    'menu-drawer',
    '.menu-drawer',
    '.mega-menu',
    'details-disclosure',
    'details-modal',
    'localization-form',
    '.disclosure-has-popup',
    '.facets',
    '.facets-vertical',
    'facet-filters-form',
    'share-button',
    '.cart-drawer',
    '.quick-order-list',
    '.header'
  ].join(',');

  var EASING = 'cubic-bezier(0.33, 0, 0.2, 1)';
  var MIN_MS = 180;
  var MAX_MS = 380;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function px(value) {
    var n = parseFloat(value);
    return isNaN(n) ? 0 : n;
  }

  /* Stopper en animation uden at dens onfinish/oncancel-handlere loeber. */
  function kill(animation) {
    if (!animation) return;
    animation.onfinish = null;
    animation.oncancel = null;
    try {
      animation.cancel();
    } catch (e) {
      /* allerede faerdig */
    }
  }

  function CFAccordion(details) {
    this.el = details;
    this.summary = details.querySelector('summary');

    // Kun summary'er der hoerer direkte til denne <details> (ikke en nestet en).
    if (!this.summary || this.summary.parentElement !== details) return;

    this.animation = null;
    this.fadeAnimations = [];
    this.isClosing = false;
    this.isExpanding = false;
    this.busy = false;

    details.cfAccordion = this;
    details.classList.add('cf-acc-anim');

    var self = this;
    this.summary.addEventListener('click', function (event) {
      self.onClick(event);
    });

    // Hvis noget udefra (fx Escape-handleren i global.js) river 'open' af,
    // saa ryd de inline-styles vi har sat, saa elementet ikke laases fast.
    // 'busy' beskytter mod et forsinket toggle-event fra en tidligere
    // animation, der ellers kunne rydde hoejden midt i den naeste.
    details.addEventListener('toggle', function () {
      if (self.busy) return;
      self.el.style.height = '';
      self.el.style.overflow = '';
    });
  }

  /* Varighed skaleret efter hvor langt der skal animeres. */
  CFAccordion.prototype.duration = function (delta) {
    var ms = Math.round(Math.abs(delta) * 0.5) + 130;
    return Math.min(Math.max(ms, MIN_MS), MAX_MS);
  };

  /* Hoejden naar den er lukket: summary + <details>' egen padding og border.
     (cf-seo__qa har fx padding paa selve <details>.) */
  CFAccordion.prototype.collapsedHeight = function () {
    var cs = window.getComputedStyle(this.el);
    return (
      this.summary.offsetHeight +
      px(cs.paddingTop) +
      px(cs.paddingBottom) +
      px(cs.borderTopWidth) +
      px(cs.borderBottomWidth)
    );
  };

  /* Hoejden naar den er aaben. Maales med overflow som i hvile, saa et
     kollapsende bundmargin i indholdet ikke giver et spring til sidst. */
  CFAccordion.prototype.expandedHeight = function () {
    var prevHeight = this.el.style.height;
    var prevOverflow = this.el.style.overflow;
    this.el.style.height = 'auto';
    this.el.style.overflow = '';
    var height = this.el.offsetHeight;
    this.el.style.height = prevHeight;
    this.el.style.overflow = prevOverflow;
    return height;
  };

  CFAccordion.prototype.contentElements = function () {
    var els = [];
    var node = this.summary.nextElementSibling;
    while (node) {
      els.push(node);
      node = node.nextElementSibling;
    }
    return els;
  };

  /* Kun ved aabning. Slutvaerdien (opacity 1) er den samme som elementets
     hviletilstand, saa der er ingen tilbagespring naar animationen slipper.
     Ved lukning fades der bevidst IKKE — indholdet glider op bag klippet. */
  CFAccordion.prototype.fadeIn = function (ms) {
    var list = this.fadeAnimations;
    this.contentElements().forEach(function (el) {
      if (typeof el.animate !== 'function') return;
      try {
        list.push(el.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: Math.min(ms, 240),
          easing: 'ease',
          fill: 'none'
        }));
      } catch (e) {
        /* ignoreres bevidst — animationen er ren pynt */
      }
    });
  };

  CFAccordion.prototype.onClick = function (event) {
    if (reducedMotion()) return; // lad browseren om det
    event.preventDefault();

    if (this.isClosing || !this.el.open) {
      this.open();
    } else {
      this.shrink();
    }
  };

  CFAccordion.prototype.open = function () {
    this.busy = true;

    // Eksklusive grupper: luk aabne soeskende bloedt foerst.
    var group = this.el.closest('[data-cf-accordion-exclusive]');
    if (group) {
      var self = this;
      Array.prototype.forEach.call(group.querySelectorAll('details[open]'), function (other) {
        if (other !== self.el && other.cfAccordion && !other.cfAccordion.isClosing) {
          other.cfAccordion.shrink();
        }
      });
    }

    this.el.style.height = this.el.offsetHeight + 'px';
    this.el.style.overflow = 'hidden';
    this.el.open = true;

    var that = this;
    window.requestAnimationFrame(function () {
      that.expand();
    });
  };

  CFAccordion.prototype.expand = function () {
    this.busy = true;
    this.isExpanding = true;
    this.isClosing = false;
    this.el.classList.remove('cf-acc-anim--closing');

    var start = this.el.offsetHeight;
    kill(this.animation);
    this.animation = null;
    this.el.style.height = start + 'px';
    this.el.style.overflow = 'hidden';

    var end = this.expandedHeight();
    if (end === start) {
      this.finish(true);
      return;
    }

    var ms = this.duration(end - start);
    var self = this;
    this.animation = this.el.animate(
      { height: [start + 'px', end + 'px'] },
      { duration: ms, easing: EASING, fill: 'forwards' }
    );
    this.fadeIn(ms);
    this.animation.onfinish = function () {
      self.finish(true);
    };
  };

  CFAccordion.prototype.shrink = function () {
    this.busy = true;
    this.isClosing = true;
    this.isExpanding = false;
    this.el.classList.add('cf-acc-anim--closing');

    var start = this.el.offsetHeight;
    kill(this.animation);
    this.animation = null;
    this.el.style.height = start + 'px';
    this.el.style.overflow = 'hidden';

    var end = this.collapsedHeight();
    if (end >= start) {
      this.finish(false);
      return;
    }

    var self = this;
    this.animation = this.el.animate(
      { height: [start + 'px', end + 'px'] },
      { duration: this.duration(start - end), easing: EASING, fill: 'forwards' }
    );
    this.animation.onfinish = function () {
      self.finish(false);
    };
  };

  /* Raekkefoelgen her er vigtig: animationen holder stadig slut-hoejden
     (fill:'forwards'), mens vi saetter den rigtige sluttilstand. Foerst
     naar [open] og de inline styles er paa plads, slipper vi animationen.
     Ellers ville elementet vise sin gamle inline-hoejde i eet billede. */
  CFAccordion.prototype.finish = function (isOpen) {
    var animation = this.animation;
    this.animation = null;

    this.el.classList.remove('cf-acc-anim--closing');
    this.el.open = isOpen;
    this.el.style.height = '';
    this.el.style.overflow = '';

    kill(animation);
    this.fadeAnimations.forEach(kill);
    this.fadeAnimations.length = 0;

    this.summary.setAttribute('aria-expanded', String(isOpen));
    this.isClosing = false;
    this.isExpanding = false;
    this.busy = false;
  };

  /* ---------- opsaetning ---------- */

  function upgrade(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(INCLUDE), function (details) {
      if (details.cfAccordion) return;
      if (details.closest(EXCLUDE)) return;
      new CFAccordion(details);
    });
  }

  function boot() {
    upgrade(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: sektioner og blokke gen-renderes.
  document.addEventListener('shopify:section:load', function (event) {
    upgrade(event.target);
  });
  document.addEventListener('shopify:block:select', function () {
    upgrade(document);
  });

  // Indhold der hentes efter sideindlaesning (fx complementary products,
  // quick view) skal ogsaa opgraderes. Debounced, saa det er billigt.
  if (window.MutationObserver) {
    var pending = null;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        upgrade(document);
      }, 250);
    });
    var start = function () {
      if (document.body) observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start);
  }
})();
